import React, { useEffect, useState, useRef } from "react";
import Decimal from "decimal.js";

import CurrencyField from "../../fields/currency_field";
import SelectField from "../../fields/select_field";
import TextField from "../../fields/text_field";
import CheckboxField from "../../fields/checkbox_field";
import { getOwners, withOwners } from "../../../constants/debt_owners";

import { VIEWS } from "../../../constants/investment_reo_views";
import FREQUENCY from "../../../constants/frequencies";
import REO_PROPERTY_TYPE from "../../../constants/reo_property_type";
import {
  DISPOSITION_STATUS,
  OCCUPANCY
} from "../../../constants/property_disposition";
import { YES_NO_TBD, YES_NO, YES, NO } from "../../../constants/yes_no";

import {
  total_misc_monthly_fees,
  total_payment,
  wrap_subject_property_if_needed
} from "../../../calculations/investment_reo";
import { default_property } from "../../../schema/investment_property";

import { useDocument } from "../../loan_tools/loan_tool";
import {
  AUTO_RETAINED_REO_TAG,
  reverse_sync_reo_to_selling_property
} from "../../../calculations/retained_residence_sync";
import Section from "../../ui/section";
import { fake_state_todo_remove } from "../../../constants/fake_state_todo_remove";

/**
 * InvestmentCalculator provides inputs for all the values of an investment
 * property.
 */
export default function InvestmentCalculator({ property_index, openView }) {
  const [document, setDocument, , { saveDocument }] = useDocument();
  const [property, setProperty] = useState({
    ...default_property,
    ...document.investment_properties[property_index]
  });
  const [subject_property] = useState({
    ...fake_state_todo_remove.property,
    ...document.property
  });
  const isSubjectProperty = property.is_subject_property === YES;
  const wrapped_property = wrap_subject_property_if_needed(subject_property, property);

  // const investmentRef = useRef({ is_modified: 0 });
  const callback = useRef();
  /*
    This hook updates callback with current state values
  */
  useEffect(() => {
    callback.current = () => {
      if (property.is_modified) {
        // todo: replace with modal
        if (property.reported_on_1040 !== "") {
          if (
            window.confirm(
              "You have unsaved changes in the Investment Property form. Would you like to save it before you leave?"
            )
          ) {
            save();
          } else {
            //do nothing
          }
        } else {
          alert(
            "Not all necessary fields were entered. Therefore changes would be dismissed."
          );
        }
      }
    };
  }, [property]);

  /*
  This hook catches unsaved changes in the form and prompts a save dialog
 */
  useEffect(() => {
    // do nothing

    // cleanup
    return () => {
      callback.current();
    };
  }, []);

  /**
   * Event fired when the cancel button is clicked
   */
  function close() {
    openView(VIEWS.PROPERTY_TABLE);
  }

  /**
   * Event fired when the next button is clicked
   */
  function saveAndNext() {
    delete property.is_modified;
    document.investment_properties[property_index] = property;

    if (property[AUTO_RETAINED_REO_TAG] === YES) {
      document.property_to_be_sold = reverse_sync_reo_to_selling_property(
        document,
        property
      );
    }

    setDocument(document);

    openView(VIEWS.SCHEDULE_E_CALCULATOR);
  }

  async function onChange(editedProperty) {
    setProperty({
      ...property,
      ...editedProperty,
      is_modified: true
    });
  }

  // Who owns this property. Same owner model as a debt: check each borrower,
  // two or more = joint. MISMO exports one arc per owner.
  const borrowers =
    (document.applicant && document.applicant.borrowers) || [];

  function toggleOwner(index, checked) {
    const current = getOwners(property);
    const key = String(index);
    let next = checked
      ? current.includes(key)
        ? current
        : [...current, key]
      : current.filter(i => i !== key);
    next.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    onChange(withOwners(property, next));
  }

  /**
   * Event fired when save button is clicked
   */
  function save() {
    delete property.is_modified;
    const investment_properties = [...document.investment_properties];
    investment_properties[property_index] = property;
    const nextDoc = { ...document, investment_properties };
    // If this is the auto-managed retained-residence entry, mirror the LO's
    // edits back to the Selling Property page so the two stay in agreement.
    if (property[AUTO_RETAINED_REO_TAG] === YES) {
      nextDoc.property_to_be_sold = reverse_sync_reo_to_selling_property(
        nextDoc,
        property
      );
    }
    setDocument(nextDoc);
    saveDocument();
    openView(VIEWS.PROPERTY_TABLE);
  }

  const showSaveButton =
    property.reported_on_1040 === NO || property.reported_on_1040 === "";

  return (
    <div className="investment-calculator">
      <Section name={`Rental Property #${property_index + 1}`}>
        <div className="flex">
          <div className="half-width">
            <SelectField
              fieldName="reported_on_1040"
              name="Reported on 1040 Schedule E Return"
              value={property.reported_on_1040}
              onChange={onChange}
              selectOptions={YES_NO}
            />
            {!isSubjectProperty && borrowers.length > 0 && (
              <div className="reo-owner-checkboxes">
                <div className="reo-owner-label">
                  Owned by — check each owner; two or more = joint
                </div>
                {borrowers.map((b, index) => (
                  <CheckboxField
                    key={index}
                    id={`reo_owner_${property_index}_${index}`}
                    name={
                      `${b.first_name || ""} ${b.last_name || ""}`.trim() ||
                      `Borrower ${index + 1}`
                    }
                    checked={getOwners(property).includes(String(index))}
                    fieldName="checked"
                    onChange={({ checked }) => toggleOwner(index, checked)}
                  />
                ))}
              </div>
            )}
            <SelectField
              fieldName="disposition_status"
              name="Status"
              value={property.disposition_status}
              onChange={onChange}
              selectOptions={DISPOSITION_STATUS}
            />
            <SelectField
              fieldName="intended_occupancy"
              name="Intended Occupancy"
              value={property.intended_occupancy}
              onChange={onChange}
              selectOptions={OCCUPANCY}
            />
            <SelectField
              fieldName="current_occupancy"
              name="Current Occupancy"
              value={property.current_occupancy}
              onChange={onChange}
              selectOptions={OCCUPANCY}
            />
            <TextField
              fieldName="year_of_purchase"
              name="Year of Purchase"
              value={property.year_of_purchase}
              onChange={onChange}
            />
            <CurrencyField
              fieldName="market_value"
              name="Market Value"
              value={wrapped_property.market_value}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <CurrencyField
              fieldName="principal_balance"
              name="Principal Balance"
              value={wrapped_property.principal_balance}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <TextField
              fieldName="property_address"
              name="Address"
              value={wrapped_property.property_address}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <TextField
              fieldName="property_address_2"
              name="Address 2"
              value={wrapped_property.property_address_2}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <TextField
              fieldName="property_city"
              name="City"
              value={wrapped_property.property_city}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <TextField
              fieldName="property_state"
              name="State"
              value={wrapped_property.property_state}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <TextField
              fieldName="property_zip"
              name="Zip"
              value={wrapped_property.property_zip}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <CurrencyField
              fieldName="first_mortgage"
              name="1st Mortgage"
              value={wrapped_property.first_mortgage}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <CurrencyField
              fieldName="second_mortgage"
              name="2nd Mortgage"
              value={wrapped_property.second_mortgage}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <CurrencyField
              fieldName="taxes"
              name="Taxes Included"
              value={wrapped_property.taxes}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <CurrencyField
              fieldName="insurance"
              name="Insurance (HO6 if Condominium)"
              value={wrapped_property.insurance}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            <CurrencyField
              readOnly
              fieldName="total_payment"
              name="Total of PITI, (HOA - MI)"
              value={total_payment(wrapped_property).toString()}
            />
            <CurrencyField
              fieldName="gross_rent_payment"
              name="Rent Received"
              value={property.gross_rent_payment}
              onChange={onChange}
            />
            {!parseFloat(property.gross_rent_payment) && (
              <div className="rent-required-note">
                <span className="req-star">*</span> Enter the rental payment to
                calculate income or loss for this property.
              </div>
            )}
            <SelectField
                fieldName="reo_property_type"
                name="Property Type"
                value={wrapped_property.reo_property_type}
                onChange={onChange}
                selectOptions={REO_PROPERTY_TYPE}
            />
            <CurrencyField
              fieldName="hoa1"
              name="HOA 1"
              value={wrapped_property.hoa1}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            {new Decimal(wrapped_property.hoa1).greaterThan(0) && (
              <SelectField
                fieldName="hoa1_frequency"
                name="HOA 1 Frequency"
                value={wrapped_property.hoa1_frequency}
                onChange={onChange}
                selectOptions={FREQUENCY}
                readOnly={isSubjectProperty}
              />
            )}
            <CurrencyField
              fieldName="hoa2"
              name="HOA 2"
              value={wrapped_property.hoa2}
              onChange={onChange}
              readOnly={isSubjectProperty}
            />
            {new Decimal(wrapped_property.hoa2).greaterThan(0) && (
              <SelectField
                fieldName="hoa2_frequency"
                name="HOA 2 Frequency"
                value={wrapped_property.hoa2_frequency}
                onChange={onChange}
                selectOptions={FREQUENCY}
                readOnly={isSubjectProperty}
              />
            )}
            <SelectField
              fieldName="mello_roos"
              name="Mello Roos / Other Fee"
              value={wrapped_property.mello_roos}
              onChange={onChange}
              selectOptions={YES_NO_TBD}
              readOnly={isSubjectProperty}
            />
            {wrapped_property.mello_roos === YES && (
              <CurrencyField
                fieldName="mello_roos_amount"
                name="Amount"
                value={wrapped_property.mello_roos_amount}
                onChange={onChange}
                readOnly={isSubjectProperty}
              />
            )}
            {wrapped_property.mello_roos === YES && (
              <SelectField
                fieldName="mello_roos_frequency"
                name="Frequency"
                value={wrapped_property.mello_roos_frequency}
                onChange={onChange}
                selectOptions={FREQUENCY}
                readOnly={isSubjectProperty}
              />
            )}
            <CurrencyField
                readOnly
                fieldName="total_misc_monthly_fees"
                name="Total of Misc Monthly Fees"
                value={total_misc_monthly_fees(wrapped_property).toString()}
            />
          </div>
          <div className="half-width">
            <div className="reo-note">
              <p>
                <strong>Note:</strong> Enter the details for each Investment,
                Second Home, or Vacation property. LANIS automatically
                calculates the required reserves for each property based on
                agency guidelines.
              </p>
              <p>
                <strong>Underwriting note:</strong> Reserves are based on the
                number of financed properties the borrowers own plus a
                percentage of each property&rsquo;s Unpaid Principal Balance
                (UPB). Review the guidelines on the Reserves page. Insufficient
                reserves can result in the loan being denied.
              </p>
            </div>
          </div>
        </div>
        <div>
          <button className="button" onClick={close}>
            Cancel
          </button>
          {property.reported_on_1040 === YES && (
            <button className="button" onClick={saveAndNext}>
              Next: Schedule E Calculator
            </button>
          )}
          {showSaveButton && (
            <button
              className="button"
              disabled={property.reported_on_1040 === ""}
              onClick={save}
            >
              Save
            </button>
          )}
        </div>
      </Section>
    </div>
  );
}
